import { LitElement, html, css, nothing } from "lit";
import { customElement, state, query } from "lit/decorators.js";
import { Router } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import type WaInput from "@awesome.me/webawesome/dist/components/input/input.js";
import { saveCgmReading, saveEntry } from "../db/operations";
import { getDb, generateId } from "../db";
import { BG_THRESHOLDS } from "../config/dafne/thresholds";
import {
  HYPO_TREATMENT,
  HYPO_TREATMENT_OPTIONS,
} from "../config/dafne/hypo-treatment";
import {
  calculateIOB,
  calculateCorrectionDose,
  IOB_CONFIG,
} from "../config/dafne/insulin";
import { getKetoneActionLevel } from "../config/dafne/ketone-alerts";
import type { UserSettings, LogEntry } from "../types";
import { DEFAULT_SETTINGS, getCurrentSickStatus, getQATotal } from "../types";

@customElement("quick-gl-page")
export class QuickGlPage extends LitElement {
  @state() private glValue = "";
  @state() private isSaving = false;
  @state() private showSaved = false;
  @state() private stayOnPage = false;
  @state() private isLoading = true;
  @state() private settings: UserSettings = { ...DEFAULT_SETTINGS };
  @state() private entries: LogEntry[] = [];
  @state() private activeIOB = 0;
  @state() private activeCorrectionIOB = 0;
  @state() private isSickDay = false;
  @state() private ketoneValue = "";

  @query("#gl-input") private glInput!: WaInput;

  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: #f8f9fa;
    }

    .page-container {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      max-width: 400px;
      margin: 0 auto;
      padding: 1rem;
    }

    .page-header {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
    }

    .page-header h1 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #1a1a1a;
    }

    .back-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: white;
      border: 1px solid #dee2e6;
      cursor: pointer;
      color: #495057;
    }

    .back-btn:hover {
      background: #e9ecef;
    }

    .iob-badge {
      margin-left: auto;
      padding: 0.25rem 0.5rem;
      background: #e7f1ff;
      color: #0d6efd;
      border-radius: 0.375rem;
      font-size: 0.75rem;
      font-weight: 600;
    }

    .ketone-section {
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px dashed #dee2e6;
    }

    .ketone-section .input-group {
      margin-bottom: 0.5rem;
    }

    .ketone-section wa-input::part(base) {
      height: 50px;
      font-size: 1.25rem;
    }

    .ketone-section wa-input::part(input) {
      font-size: 1.25rem;
    }

    .ketone-note {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      background: #e7f1ff;
      border-radius: 0.375rem;
      color: #0d6efd;
      font-size: 0.8rem;
      margin-bottom: 0.75rem;
    }

    .ketone-note wa-icon {
      font-size: 1rem;
    }

    .form-section {
      background: white;
      border-radius: 1rem;
      padding: 1.5rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .input-group {
      margin-bottom: 1rem;
    }

    .input-label {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      font-weight: 500;
      color: #495057;
      margin-bottom: 0.5rem;
    }

    .input-label wa-icon {
      font-size: 1rem;
    }

    /* Large input styling for easy phone use */
    wa-input::part(base) {
      border-radius: 0.75rem;
      border: 2px solid #dee2e6;
      font-size: 1.5rem;
      height: 60px;
    }

    wa-input::part(input) {
      font-size: 1.5rem;
      text-align: center;
      font-weight: 600;
    }

    wa-input::part(base):focus-within {
      border-color: #0d6efd;
      box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.25);
    }

    .unit-hint {
      font-size: 0.75rem;
      color: #6c757d;
      text-align: center;
      margin-top: 0.25rem;
    }

    .help-text {
      font-size: 0.875rem;
      color: #6c757d;
      margin-top: 1rem;
    }

    .actions {
      margin-top: 1.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .save-btn::part(base) {
      height: 56px;
      font-size: 1.125rem;
      font-weight: 600;
      border-radius: 0.75rem;
    }

    .quick-actions {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;
      margin-top: 1.5rem;
    }

    .quick-btn {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.25rem;
      padding: 1rem;
      background: #f8f9fa;
      border: 1px solid #dee2e6;
      border-radius: 0.75rem;
      cursor: pointer;
      color: #495057;
      font-size: 0.875rem;
      font-weight: 500;
    }

    .quick-btn:hover {
      background: #e9ecef;
    }

    .quick-btn wa-icon {
      font-size: 1.5rem;
    }

    .success-banner {
      position: fixed;
      top: 1rem;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1000;
    }

    .warning-section {
      margin-top: 1rem;
    }

    .warning-section wa-callout {
      margin-bottom: 0.75rem;
    }

    .warning-section wa-callout::part(base) {
      font-size: 0.875rem;
    }

    .treatment-options {
      margin-top: 0.5rem;
      font-size: 0.8rem;
      color: inherit;
    }

    .treatment-options ul {
      margin: 0.25rem 0 0 0;
      padding-left: 1.25rem;
    }

    .treatment-options li {
      margin-bottom: 0.125rem;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    // Check for stay=true in URL
    const params = new URLSearchParams(window.location.search);
    this.stayOnPage = params.get("stay") === "true";

    // Load data for recommendations
    this._loadData();
  }

  private async _loadData() {
    this.isLoading = true;
    try {
      await Promise.all([this._loadSettings(), this._loadEntries()]);
      this._calculateIOB();
      this._checkSickDay();
    } catch (error) {
      console.error("Failed to load data for Quick GL:", error);
    } finally {
      this.isLoading = false;
    }
  }

  private async _loadSettings() {
    try {
      const stored = await getDb().settings.get("settings");
      if (stored) this.settings = stored;
    } catch (error) {
      console.error("Failed to load settings:", error);
    }
  }

  private async _loadEntries() {
    const now = new Date();
    // Look back 5 hours for IOB calculation
    const windowStart = now.getTime() - 5 * 60 * 60 * 1000;
    const nowTs = now.getTime();

    const getTs = (t: Date | string | number | unknown): number => {
      if (t instanceof Date) return t.getTime();
      if (typeof t === "string") return new Date(t).getTime();
      if (typeof t === "number") return t;
      return new Date(t as string).getTime();
    };

    try {
      const allEntries = await getDb().entries.toArray();
      this.entries = allEntries.filter((e) => {
        if ((e as any)._deletedAt) return false;
        const ts = getTs(e.timestamp);
        return ts >= windowStart && ts <= nowTs;
      });
    } catch (error) {
      console.error("Failed to load entries:", error);
    }
  }

  private _calculateIOB() {
    const now = new Date();
    const nowTs = now.getTime();
    let totalIOB = 0;
    let correctionIOB = 0;

    const getTs = (t: Date | string | number | unknown): number => {
      if (t instanceof Date) return t.getTime();
      if (typeof t === "string") return new Date(t).getTime();
      if (typeof t === "number") return t;
      return new Date(t as string).getTime();
    };

    for (const entry of this.entries) {
      const qaTotal = getQATotal(entry);
      if (!qaTotal || qaTotal <= 0) continue;

      const hoursElapsed = (nowTs - getTs(entry.timestamp)) / (1000 * 60 * 60);
      const iob = calculateIOB(qaTotal, hoursElapsed);
      totalIOB += iob;

      // Track correction IOB separately (insulin without carbs)
      const hasCarbs = (entry.carbs ?? 0) > 0;
      if (!hasCarbs) {
        correctionIOB += iob;
      } else if (entry.qaCorrection && entry.qaCorrection > 0) {
        // If it had carbs but also correction, count that portion
        correctionIOB += calculateIOB(entry.qaCorrection, hoursElapsed);
      }
    }

    this.activeIOB = Math.round(totalIOB * 10) / 10;
    this.activeCorrectionIOB = Math.round(correctionIOB * 10) / 10;
  }

  private _checkSickDay() {
    const sickStatus = getCurrentSickStatus(this.entries);
    this.isSickDay = sickStatus !== null;
  }

  private _handleBack() {
    Router.go("/");
  }

  private async _handleSave() {
    const gl = parseFloat(this.glValue);

    if (isNaN(gl) || gl < 1 || gl > 33) {
      return;
    }

    this.isSaving = true;

    try {
      const now = new Date();

      // Save as manual CGM reading
      await saveCgmReading({
        timestamp: now,
        glucose: gl,
        type: "manual",
        source: "manual",
        createdAt: now,
      });

      // If ketones were entered, save as a LogEntry as well
      const ketones = parseFloat(this.ketoneValue);
      if (!isNaN(ketones) && ketones >= 0 && ketones <= 10) {
        const entry: LogEntry = {
          id: generateId(),
          timestamp: now,
          periodTag: "Other", // Quick GL entries don't map to specific meal periods
          bloodGlucose: gl,
          ketones: ketones,
          entryContext: "live",
          notes: "Quick GL entry",
          createdAt: now,
          updatedAt: now,
        };
        await saveEntry(entry);
      }

      this.showSaved = true;

      // Reset form
      this.glValue = "";
      this.ketoneValue = "";

      // Navigate back if not staying
      if (!this.stayOnPage) {
        setTimeout(() => {
          Router.go("/");
        }, 500);
      } else {
        setTimeout(() => {
          this.showSaved = false;
          // Focus GL input for next entry
          this.glInput?.focus();
        }, 1500);
      }
    } catch (err) {
      console.error("Failed to save GL reading:", err);
    } finally {
      this.isSaving = false;
    }
  }

  private _renderRecommendations() {
    const gl = parseFloat(this.glValue);
    if (isNaN(gl)) return nothing;

    const sections = [];
    const cf = this.settings.correctionFactor || 2.5;
    const targetGL = 6.0; // DAFNE standard target

    // Calculate what correction would be needed (before accounting for IOB)
    const rawCorrection = calculateCorrectionDose(gl, targetGL, cf, 0);
    const netCorrection = calculateCorrectionDose(
      gl,
      targetGL,
      cf,
      this.activeCorrectionIOB,
    );

    // 1. HYPO (GL < 4.0) - URGENT
    if (gl < BG_THRESHOLDS.HYPO) {
      sections.push(html`
        <wa-callout variant="danger" open>
          <wa-icon name="triangle-exclamation" slot="icon"></wa-icon>
          <strong>Hypo Alert (${gl.toFixed(1)} mmol/L)</strong>
          <p>
            Based on OZDAFNE guidelines, consider ${HYPO_TREATMENT.TREATMENT_GRAMS}g fast-acting carbs:
          </p>
          <div class="treatment-options">
            <ul>
              ${HYPO_TREATMENT_OPTIONS.slice(0, 4).map(
                (opt) => html`<li>${opt.name} (${opt.amount})</li>`,
              )}
            </ul>
            <p>Wait ${HYPO_TREATMENT.WAIT_MINUTES} minutes and recheck.</p>
          </div>
          ${this.activeIOB > 0
            ? html`<p style="margin-top: 0.5rem; font-weight: 500;">
                ⚠️ You have ${this.activeIOB}u active insulin - may need
                additional carbs.
              </p>`
            : nothing}
          <p style="margin-top: 0.5rem; color: inherit;">
            <strong>Correction insulin is not recommended at this time, based on OZDAFNE guidelines.</strong>
          </p>
        </wa-callout>
      `);
      // Return early for hypo - most important
      return html`<div class="warning-section">${sections}</div>`;
    }

    // 2. LOW-ISH (4.0-5.0) - Watch closely
    if (gl >= 4.0 && gl < 5.0) {
      if (this.activeIOB > 0.5) {
        sections.push(html`
          <wa-callout variant="warning" open>
            <wa-icon name="circle-info" slot="icon"></wa-icon>
            <strong>GL Near Hypo Range</strong>
            <p>
              You have <strong>${this.activeIOB}u</strong> active insulin which
              may cause further drop. Consider a small snack (5-10g carbs) if
              you're not eating soon.
            </p>
          </wa-callout>
        `);
      } else {
        sections.push(html`
          <wa-callout variant="neutral" open>
            <wa-icon name="circle-info" slot="icon"></wa-icon>
            <strong>GL in Lower Range (${gl.toFixed(1)} mmol/L)</strong>
            <p>
              Good to monitor. Consider timing of your next meal to maintain
              levels.
            </p>
          </wa-callout>
        `);
      }
    }

    // 3. TARGET RANGE (5.0-7.0) - All good
    if (gl >= 5.0 && gl <= 7.0) {
      sections.push(html`
        <wa-callout variant="success" open>
          <wa-icon name="circle-check" slot="icon"></wa-icon>
          <strong>GL in Target Range ✓</strong>
          <p>
            ${gl.toFixed(1)} mmol/L is within the target range (4-7 mmol/L).
            ${this.activeIOB > 0
              ? html`You have ${this.activeIOB}u active insulin.`
              : nothing}
          </p>
        </wa-callout>
      `);
    }

    // 4. ELEVATED (7.0-10.0) - May need correction
    if (gl > 7.0 && gl < 10.0) {
      if (this.activeCorrectionIOB >= rawCorrection) {
        sections.push(html`
          <wa-callout variant="neutral" open>
            <wa-icon name="clock" slot="icon"></wa-icon>
            <strong>Slightly Elevated (${gl.toFixed(1)} mmol/L)</strong>
            <p>
              Your active correction insulin
              (${this.activeCorrectionIOB.toFixed(1)}u) may help bring GL
              down. Consider waiting 2-3 hours before additional correction.
            </p>
          </wa-callout>
        `);
      } else if (netCorrection > 0.5) {
        sections.push(html`
          <wa-callout variant="warning" open>
            <wa-icon name="syringe" slot="icon"></wa-icon>
            <strong>Correction May Help</strong>
            <p>
              GL ${gl.toFixed(1)} mmol/L is above target.
              ${this.activeCorrectionIOB > 0
                ? html`After accounting for
                  ${this.activeCorrectionIOB.toFixed(1)}u active correction
                  insulin, you`
                : html`You`}
              may benefit from <strong>${netCorrection.toFixed(1)}u</strong>
              correction insulin.
            </p>
            <p style="font-size: 0.8rem; margin-top: 0.5rem; opacity: 0.8;">
              Based on your correction factor of 1u per ${cf} mmol/L
            </p>
          </wa-callout>
        `);
      } else {
        sections.push(html`
          <wa-callout variant="neutral" open>
            <wa-icon name="circle-info" slot="icon"></wa-icon>
            <strong>Slightly Elevated (${gl.toFixed(1)} mmol/L)</strong>
            <p>
              ${this.activeIOB > 0
                ? html`Active insulin (${this.activeIOB.toFixed(1)}u) should
                  help.`
                : html`May improve naturally with activity or time.`}
            </p>
          </wa-callout>
        `);
      }
    }

    // 5. HIGH (10.0-15.0) - Correction recommended
    if (gl >= 10.0 && gl < BG_THRESHOLDS.KETONE_CHECK_TRIGGER) {
      const sickMultiplier = this.isSickDay ? 1.1 : 1.0;
      const adjustedCorrection = netCorrection * sickMultiplier;

      if (netCorrection > 0.5) {
        sections.push(html`
          <wa-callout variant="warning" open>
            <wa-icon name="syringe" slot="icon"></wa-icon>
            <strong>High GL - Correction Recommended</strong>
            <p>
              GL ${gl.toFixed(1)} mmol/L requires attention.
              ${this.activeCorrectionIOB > 0
                ? html`After ${this.activeCorrectionIOB.toFixed(1)}u active
                  correction IOB,`
                : nothing}
              suggest
              <strong>${adjustedCorrection.toFixed(1)}u</strong> correction.
              ${this.isSickDay
                ? html`<span style="color: #856404;">(+10% for sick day)</span>`
                : nothing}
            </p>
            <p style="font-size: 0.8rem; margin-top: 0.5rem; opacity: 0.8;">
              Using your correction factor: 1u per ${cf} mmol/L
            </p>
          </wa-callout>
        `);
      } else {
        sections.push(html`
          <wa-callout variant="neutral" open>
            <wa-icon name="clock" slot="icon"></wa-icon>
            <strong>High GL (${gl.toFixed(1)} mmol/L)</strong>
            <p>
              Active correction insulin
              (${this.activeCorrectionIOB.toFixed(1)}u) covers this. Wait 2-3
              hours to see effect.
            </p>
          </wa-callout>
        `);
      }
    }

    // 6. VERY HIGH (≥15.0) - Ketone check + correction
    if (gl >= BG_THRESHOLDS.KETONE_CHECK_TRIGGER) {
      const sickMultiplier = this.isSickDay ? 1.2 : 1.0;
      const adjustedCorrection = Math.max(0, netCorrection * sickMultiplier);

      sections.push(html`
        <wa-callout variant="danger" open>
          <wa-icon name="triangle-exclamation" slot="icon"></wa-icon>
          <strong>Very High GL (${gl.toFixed(1)} mmol/L)</strong>
          <div style="margin-top: 0.5rem;">
            <p><strong>1. Check ketones</strong> if you haven't recently</p>
            ${adjustedCorrection > 0.5
              ? html`<p style="margin-top: 0.5rem;">
                  <strong>2. Correction needed:</strong>
                  ${adjustedCorrection.toFixed(1)}u
                  ${this.isSickDay
                    ? html`<span style="color: inherit;"
                        >(+20% for sick day)</span
                      >`
                    : nothing}
                </p>`
              : html`<p style="margin-top: 0.5rem;">
                  <strong>2.</strong> Active IOB
                  (${this.activeCorrectionIOB.toFixed(1)}u) may help - monitor
                  closely
                </p>`}
            <p style="margin-top: 0.5rem;">
              <strong>3. Consider staying hydrated</strong>
            </p>
          </div>
        </wa-callout>
      `);

      // Add sick day extra guidance if applicable
      if (this.isSickDay) {
        sections.push(html`
          <wa-callout variant="warning" open>
            <wa-icon name="face-thermometer" slot="icon"></wa-icon>
            <strong>Sick Day Mode Active</strong>
            <p>
              DAFNE sick day rules: Use 10-20% extra on corrections. Check GL
              every 2-4 hours. Never skip insulin even if not eating.
            </p>
          </wa-callout>
        `);
      }
    }

    // 7. IOB Status (show if significant IOB and not already covered above)
    if (this.activeIOB > 1 && gl >= 5.0 && gl < 10.0) {
      const hoursRemaining = Math.round(
        (this.activeIOB / 2) * IOB_CONFIG.QA_DURATION_HOURS,
      ); // Rough estimate
      sections.push(html`
        <wa-callout variant="brand" open>
          <wa-icon name="clock" slot="icon"></wa-icon>
          <strong>Active Insulin: ${this.activeIOB}u</strong>
          <p>
            This insulin is still working and will continue for
            ~${Math.min(hoursRemaining, 4)} more hours. Factor this in before
            taking more insulin.
          </p>
        </wa-callout>
      `);
    }

    if (sections.length === 0) return nothing;

    return html`<div class="warning-section">${sections}</div>`;
  }

  private _renderKetoneAlerts() {
    const ketones = parseFloat(this.ketoneValue);
    if (isNaN(ketones) || ketones < 0) return nothing;

    const level = getKetoneActionLevel(ketones);

    // Map level to callout variant
    const variantMap: Record<string, string> = {
      normal: "success",
      elevated: "warning",
      high: "danger",
      danger: "danger",
    };

    const iconMap: Record<string, string> = {
      normal: "circle-check",
      elevated: "triangle-exclamation",
      high: "triangle-exclamation",
      danger: "hexagon-exclamation",
    };

    return html`
      <div class="warning-section" style="margin-top: 0.75rem;">
        <wa-callout variant="${variantMap[level.level]}" open>
          <wa-icon name="${iconMap[level.level]}" slot="icon"></wa-icon>
          <strong>${ketones.toFixed(1)} mmol/L - ${level.action}</strong>
          ${level.details.length > 0
            ? html`
                <ul style="margin: 0.5rem 0 0 0; padding-left: 1.25rem;">
                  ${level.details.map((d) => html`<li>${d}</li>`)}
                </ul>
              `
            : nothing}
          ${level.recheckMinutes
            ? html`<p style="margin-top: 0.5rem; font-size: 0.8rem;">
                Recheck ketones in ${Math.round(level.recheckMinutes / 60)}
                hours
              </p>`
            : nothing}
          ${level.seekHelp
            ? html`<p
                style="margin-top: 0.5rem; font-weight: 600; color: #dc3545;"
              >
                ⚠️ Contact your diabetes team or seek medical help
              </p>`
            : nothing}
        </wa-callout>
      </div>
    `;
  }

  private _renderKetoneInput() {
    const gl = parseFloat(this.glValue);
    const shouldShowKetone =
      !isNaN(gl) && gl >= BG_THRESHOLDS.KETONE_CHECK_TRIGGER;

    if (!shouldShowKetone) {
      return nothing;
    }

    // Show ketone input automatically when GL >= 15
    return html`
      <div class="ketone-section">
        <div class="ketone-note">
          <wa-icon name="circle-info"></wa-icon>
          Check ketones when GL is ${BG_THRESHOLDS.KETONE_CHECK_TRIGGER}+ mmol/L
        </div>
        <div class="input-group">
          <div class="input-label">
            <wa-icon name="vial"></wa-icon>
            Ketone Reading (optional)
          </div>
          <wa-input
            type="number"
            inputmode="decimal"
            min="0"
            max="10"
            step="0.1"
            placeholder="e.g. 0.5"
            .value=${this.ketoneValue}
            @input=${(e: Event) =>
              (this.ketoneValue = (e.target as HTMLInputElement).value)}
          ></wa-input>
          <div class="unit-hint">mmol/L</div>
        </div>
        ${this._renderKetoneAlerts()}
      </div>
    `;
  }

  render() {
    const canSave =
      this.glValue && !isNaN(parseFloat(this.glValue)) && !this.isSaving;

    return html`
      <div class="page-container">
        ${this.showSaved
          ? html`
              <wa-callout variant="success" open class="success-banner">
                <wa-icon name="circle-check" slot="icon"></wa-icon>
                GL saved!
              </wa-callout>
            `
          : nothing}

        <div class="page-header">
          <button class="back-btn" @click=${this._handleBack}>
            <wa-icon name="arrow-left"></wa-icon>
          </button>
          <h1>Quick GL</h1>
          ${this.isLoading
            ? html`<wa-spinner style="font-size: 1rem;"></wa-spinner>`
            : this.activeIOB > 0
              ? html`<span class="iob-badge"
                  >IOB: ${this.activeIOB.toFixed(1)}u</span
                >`
              : nothing}
        </div>

        <div class="form-section">
          <div class="input-group">
            <div class="input-label">
              <wa-icon name="droplet"></wa-icon>
              Current GL Reading
            </div>
            <wa-input
              id="gl-input"
              type="number"
              inputmode="decimal"
              min="1"
              max="33"
              step="0.1"
              .value=${this.glValue}
              @input=${(e: Event) =>
                (this.glValue = (e.target as HTMLInputElement).value)}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter" && canSave) this._handleSave();
              }}
              autofocus
            ></wa-input>
            <div class="unit-hint">mmol/L</div>
          </div>

          ${this._renderRecommendations()} ${this._renderKetoneInput()}
          ${!this.glValue
            ? html`<p class="help-text">
                Enter your current glucose for DAFNE-based recommendations.
              </p>`
            : nothing}

          <div class="actions">
            <wa-button
              class="save-btn"
              variant="brand"
              size="large"
              ?disabled=${!canSave}
              ?loading=${this.isSaving}
              @click=${this._handleSave}
            >
              ${this.isSaving ? "Saving..." : "Save GL"}
            </wa-button>
          </div>
        </div>

        <div class="quick-actions">
          <button class="quick-btn" @click=${() => Router.go("/add")}>
            <wa-icon name="book-medical"></wa-icon>
            Add Entry
          </button>
          <button class="quick-btn" @click=${() => Router.go("/history")}>
            <wa-icon name="chart-bar"></wa-icon>
            History
          </button>
        </div>
      </div>
    `;
  }
}
